import { Currency } from "@redotech/money/iso4217/currencies";
import { normalizeText } from "@redotech/util/string";
import * as z from "zod";

export const PO_CSV_HEADER_LABELS = {
  PO_NUMBER: "PO Number",
  SUPPLIER: "Supplier",
  LOCATION_NAME: "Location name",
  REFERENCE: "Reference",
  TAGS: "Tags",
  CURRENCY: "Currency",
  NOTE: "Note",
} as const;

export const PO_CSV_PRODUCT_COLUMNS = {
  SKU: "SKU",
  PRODUCT_TITLE: "Product Title",
  QUANTITY: "Quantity",
  UNIT_COST: "Unit Cost",
  TAX: "Tax",
} as const;

const LABELS = PO_CSV_HEADER_LABELS;
const COLS = PO_CSV_PRODUCT_COLUMNS;

const CURRENCY_CODES = new Set<string>(Object.values(Currency));

const CurrencySchema = z.preprocess(
  (val) => {
    if (typeof val !== "string") {
      return val;
    }
    const trimmed = val.trim();
    return trimmed === "" ? undefined : trimmed.toUpperCase();
  },
  z
    .string()
    .refine((code) => CURRENCY_CODES.has(code), {
      message: `"${LABELS.CURRENCY}" must be a valid 3-letter code (e.g. USD)`,
    })
    .optional(),
);

const TagsSchema = z.preprocess(
  (val) => {
    if (typeof val !== "string") {
      return val;
    }
    const tags = val
      .split(",")
      .map((tag) => tag.trim())
      .filter((tag) => tag !== "");
    return tags.length === 0 ? undefined : tags;
  },
  z.array(z.string().max(255)).optional(),
);

export const PoCsvHeader = z.object({
  poNumber: z.string().max(255).optional(),
  supplierName: z.string().max(255).optional(),
  locationName: z.string().max(255).optional(),
  reference: z.string().max(255).optional(),
  tags: TagsSchema,
  currency: CurrencySchema,
  note: z.string().max(1000).optional(),
});
export type PoCsvHeader = z.infer<typeof PoCsvHeader>;

export function normalizePoCsvName(name: string): string {
  return normalizeText(name.replace(/,/g, " "));
}

const QuantitySchema = z.preprocess(
  (val) => {
    if (val == null || val === "") {
      return undefined;
    }
    if (typeof val === "string") {
      const n = Number(val.trim());
      return Number.isNaN(n) ? val : n;
    }
    return val;
  },
  z.number().int().positive(`"${COLS.QUANTITY}" must be a positive integer`),
);

const UnitCostSchema = z.preprocess(
  (val) => {
    if (val == null || val === "") {
      return undefined;
    }
    if (typeof val === "string") {
      const n = parseFloat(val.trim());
      return Number.isFinite(n) ? n : val;
    }
    return val;
  },
  z
    .number()
    .nonnegative(`"${COLS.UNIT_COST}" must be a non-negative number`)
    .optional(),
);

const TaxSchema = z.preprocess(
  (val) => {
    if (val == null || val === "") {
      return undefined;
    }
    if (typeof val === "string") {
      const n = parseFloat(val.trim());
      return Number.isFinite(n) ? n : val;
    }
    return val;
  },
  z
    .number()
    .nonnegative(`"${COLS.TAX}" must be a non-negative number`)
    .optional(),
);

export const PoCsvProductRow = z.object({
  sku: z.string().min(1, `"${COLS.SKU}" is required`).max(255),
  productTitle: z.string().max(255).optional(),
  quantity: QuantitySchema,
  unitCost: UnitCostSchema,
  tax: TaxSchema,
});
export type PoCsvProductRow = z.infer<typeof PoCsvProductRow>;

// First-match fallback depends on caller ordering — pass a stably-ordered list.
export function chooseProductForSku<T extends { title: string }>(
  candidates: readonly T[],
  productTitle: string | undefined,
): T | undefined {
  if (candidates.length <= 1 || !productTitle) {
    return candidates[0];
  }
  const wanted = productTitle.trim().toLowerCase();
  return (
    candidates.find((c) => c.title.trim().toLowerCase() === wanted) ??
    candidates[0]
  );
}

export interface SupplierNameCandidate {
  id: bigint;
  name: string;
}

export type SupplierNameMatch =
  | { kind: "exact"; id: bigint }
  | { kind: "suggestions"; suggestions: string[] };

function supplierNameTokens(name: string): Set<string> {
  return new Set(
    name.toLowerCase().replaceAll(/[.,]/g, " ").split(/\s+/).filter(Boolean),
  );
}

export function matchSupplierName(
  candidates: readonly SupplierNameCandidate[],
  rawName: string,
): SupplierNameMatch {
  const normalizedName = rawName.trim().toLowerCase();
  const exact = candidates.find(
    (candidate) => candidate.name.trim().toLowerCase() === normalizedName,
  );
  if (exact) {
    return { kind: "exact", id: exact.id };
  }

  const rawTokens = supplierNameTokens(rawName);
  if (rawTokens.size === 0) {
    return { kind: "suggestions", suggestions: [] };
  }

  const suggestions = candidates
    .map((candidate) => ({
      name: candidate.name,
      overlap: [...supplierNameTokens(candidate.name)].filter((token) =>
        rawTokens.has(token),
      ).length,
    }))
    .filter((candidate) => candidate.overlap > 0)
    .sort((a, b) => b.overlap - a.overlap || a.name.localeCompare(b.name))
    .slice(0, 5)
    .map((candidate) => candidate.name);

  return { kind: "suggestions", suggestions };
}

export type PoCsvRowResultStatus = "created" | "error";

export const PoCsvRowResult = z.object({
  status: z.enum(["created", "error"]),
  rowNumber: z.number().int().positive(),
  sku: z.string(),
  poNumber: z.string().optional(),
  message: z.string().optional(),
  purchaseOrderId: z.string().optional(),
});
export type PoCsvRowResult = z.infer<typeof PoCsvRowResult>;

export const ImportPoCsvCounts = z.object({
  totalCount: z.number().int().nonnegative(),
  createdCount: z.number().int().nonnegative(),
  errorCount: z.number().int().nonnegative(),
});
export type ImportPoCsvCounts = z.infer<typeof ImportPoCsvCounts>;

export const ImportPoCsvResult = ImportPoCsvCounts.extend({
  results: z.array(PoCsvRowResult),
  purchaseOrderIds: z.array(z.string()),
  parseErrors: z.array(z.string()),
});
export type ImportPoCsvResult = z.infer<typeof ImportPoCsvResult>;

export const ImportPoCsvFileResult = ImportPoCsvResult.extend({
  fileName: z.string(),
});
export type ImportPoCsvFileResult = z.infer<typeof ImportPoCsvFileResult>;

export const ImportPoCsvBatchResult = z.object({
  files: z.array(ImportPoCsvFileResult),
});
export type ImportPoCsvBatchResult = z.infer<typeof ImportPoCsvBatchResult>;

const HEADER_LABEL_BY_LOWER = new Map<string, string>(
  Object.values(LABELS).map((label) => [label.toLowerCase(), label]),
);

interface ParsedPoCsvProductRow {
  rowNumber: number;
  raw: Record<string, string>;
}

interface ParsePoCsvRowsResult {
  header: Record<string, string>;
  productRows: ParsedPoCsvProductRow[];
  parseErrors: string[];
}

export function parsePoCsvRows(
  rows: readonly (readonly string[])[],
): ParsePoCsvRowsResult {
  const header: Record<string, string> = {};
  const productRows: ParsedPoCsvProductRow[] = [];
  const parseErrors: string[] = [];

  let productHeaderIndex = -1;
  let columnIndices: Record<string, number> = {};

  for (const [i, row_] of rows.entries()) {
    const row = row_ ?? [];
    const first = (row[0] ?? "").trim();

    if (
      row.some(
        (cell) => (cell ?? "").trim().toLowerCase() === COLS.SKU.toLowerCase(),
      )
    ) {
      productHeaderIndex = i;
      columnIndices = {};
      for (const col of Object.values(COLS)) {
        const index = row.findIndex(
          (cell) => (cell ?? "").trim().toLowerCase() === col.toLowerCase(),
        );
        if (index >= 0) {
          columnIndices[col] = index;
        }
      }
      if (
        columnIndices[COLS.SKU] == null ||
        columnIndices[COLS.QUANTITY] == null
      ) {
        parseErrors.push(
          `The product header row must include "${COLS.SKU}" and "${COLS.QUANTITY}".`,
        );
        return { header, productRows, parseErrors };
      }
      break;
    }

    const canonicalLabel = HEADER_LABEL_BY_LOWER.get(first.toLowerCase());
    if (canonicalLabel) {
      header[canonicalLabel] = (row[1] ?? "").trim();
    }
  }

  if (productHeaderIndex < 0) {
    parseErrors.push(
      `Missing the product header row (${COLS.SKU}, ${COLS.QUANTITY}, ${COLS.UNIT_COST}, ${COLS.TAX}).`,
    );
    return { header, productRows, parseErrors };
  }

  for (let i = productHeaderIndex + 1; i < rows.length; i++) {
    const row = rows[i] ?? [];
    const skuIndex = columnIndices[COLS.SKU]!;
    const sku = (row[skuIndex] ?? "").trim();

    const hasContent = row.some((cell) => (cell ?? "").trim() !== "");
    if (!hasContent) {
      continue;
    }
    if (sku === "") {
      continue;
    }

    const raw: Record<string, string> = {};
    for (const col of Object.values(COLS)) {
      const index = columnIndices[col];
      raw[col] = index == null ? "" : (row[index] ?? "").trim();
    }
    productRows.push({ rowNumber: i + 1, raw });
  }

  return { header, productRows, parseErrors };
}

export const PO_CSV_TEMPLATE_LINES: readonly (readonly string[])[] = [
  [LABELS.PO_NUMBER, ""],
  [LABELS.SUPPLIER, "Main Supplier"],
  [LABELS.LOCATION_NAME, "Main Location"],
  [LABELS.REFERENCE, ""],
  [LABELS.TAGS, ""],
  [LABELS.CURRENCY, "USD"],
  [LABELS.NOTE, ""],
  [],
  [COLS.SKU, COLS.PRODUCT_TITLE, COLS.QUANTITY, COLS.UNIT_COST, COLS.TAX],
  ["ABC-100", "", "100", "12.50", "0"],
  ["ABC-200", "", "50", "9.99", "0"],
];
